import * as React from 'react'
import { memo } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'
import { getLabel, getValue, uniformUrl } from '../helpers/StringHelpers'
import { USD_P2 } from '../helpers/NumberHelper'
import { getProductDetails } from '../common/product'
import finalPrice from '../helpers/product/finalPrice'
import { BAND_TYPE, computeShipping, SILVER_SET_TYPES } from '../common/checkout'
import { getImageSize, IMAGE_TYPES } from '../common/gatsbyImageData'

const ProductStructuredMetadata = ({ site, product }) => {
  const getLastDayOfMonthEST =() => {
    const now = new Date() // Get the current date
    const estOffset = -5 // EST timezone offset in hours
    const estTime = now.getTime() + now.getTimezoneOffset() * 60000 + estOffset * 3600000 // Convert to EST time
    const estDate = new Date(estTime)

    const year = estDate.getFullYear()
    const month = estDate.getMonth()
    const lastDay = new Date(year, month + 1, 0) // Create a new Date object with the last day of the current month

    return lastDay
  }
  const priceValidUntil = getLastDayOfMonthEST()
  
  const { unstructured: attributes } = getProductDetails(product)
  return JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      sku: product.sku,
      mpn: product.model_no || product.sku,
      description: product.description,
      image: product?.image?.url || site.logo,
      url: `${site.siteUrl}${product.url}/`,
      offers: [
        {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: finalPrice(product),
          availability:
            product.qty > 0
              ? 'https://schema.org/InStock'
              : 'https://schema.org/LimitedAvailability',
          seller: {
            '@type': 'Organization',
            name: site.name,
          },
          url: `${site.siteUrl}${product.url}/`,
          priceValidUntil: `${(product.special_to_date || priceValidUntil).toISOString().substring(0,10)}T07:00:00.000Z`,
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'US',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 10,
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn',
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: 'US',
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              businessDays: {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: [
                  'https://schema.org/Monday',
                  'https://schema.org/Tuesday',
                  'https://schema.org/Wednesday',
                  'https://schema.org/Thursday',
                ],
              },
              cutoffTime: '20:00:00Z',
              handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 1,
                maxValue: 2,
                unitCode: 'd',
              },
              transitTime: {
                '@type': 'QuantitativeValue',
                minValue: 1,
                maxValue: 1,
                unitCode: 'd',
              },
            },
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: computeShipping(finalPrice(product), 
                product.item_type === BAND_TYPE,
                SILVER_SET_TYPES.includes(product.item_category)
              ),
              currency: 'USD',
            },
          },
        },
      ],
      brand: product.brand,
      model: product.model || product.item_type || product.item_category,
      material: (product?.metal_name?.metals || []).join(', '),
      positiveNotes: attributes.map((attribute, i) => {
        return {
          '@type': 'ListItem',
          position: i + 1,
          item: {
            name: getLabel(attribute),
            alternateName: attribute,
            description: getValue(attribute, product[attribute]),
          },
        }
      }),
      // TODO: consider adding reviews (maybe query brand / model names from Google Reviews?)
      // Try using this code snippet:
      /*
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": 5,
        "bestRating": 5,
        "reviewCount": 21
      },
      "review": {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Jackie March"
        }
      }
      */
    },
    null,
    2
  )
}

const WebPageStructuredMetadata = ({ site }) => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: site.title,
    alternateName: `${site.alternateName}`,
    url: `${site.siteUrl}/`,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${site.siteUrl}/search/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
      name: 'Search',
    },
  })
}

const LocalBusinessStructuredMetadata = ({ site }) => {
  // (vide: https://www.gatsbyjs.com/docs/how-to/adding-common-features/seo/)
  return JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': site.businessType,
    address: {
      '@type': 'PostalAddress',
      addressLocality: site.addressCity,
      addressRegion: site.addressState,
      postalCode: site.addressZip,
      streetAddress: site.addressStreet,
    },
    name: site.name,
    url: site.siteUrl,
    telephone: site.phone,
    email: site.email,
    priceRange: site.priceRange,
    founder: site.author,
    image: site.image,
    foundingDate: site.foundingYear,
    legalName: site.legalName,
    logo: site.logo,
    areaServed: {
      '@type': 'State',
      name: 'Florida',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 25.8869068,
      longitude: -80.1236891,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-305-770-6955',
      contactType: 'Selling Department of Gray and Sons',
      availableLanguage: ['English', 'Spanish', 'Russian', 'Portuguese', 'Polish'],
    },
    paymentAccepted: 'Cash, Credit Card (American Express, Visa, Mastercard), PayPal, Affirm',
    openingHours: 'Mo,Tu,We,Th,Fr,Sa 10:00-17:00',
  })
}

const OrganizationStructuredMetadata = ({ site, reviews }) => {
  return JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'Organization',
    address: {
      '@type': 'PostalAddress',
      addressLocality: site.addressCity,
      addressRegion: site.addressState,
      postalCode: site.addressZip,
      streetAddress: site.addressStreet,
    },
    name: site.name,
    url: `${site.siteUrl}/`,
    telephone: site.phone,
    email: site.email,
    founder: site.author,
    image: site.image,
    foundingDate: site.foundingYear,
    legalName: site.legalName,
    logo: site.logo,
    areaServed: {
      '@type': 'State',
      name: 'Florida',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: (reviews.sum / reviews.total).toFixed(1),
      reviewCount: reviews.total,
    },
    sameAs: [
      site.facebook,
      site.twitter,
      site.linkedin,
      site.instagram,
      site.youtube,
      site.pinterest,
    ],
  })
}

const getDate = (article, product, data) => {
  if (article) return article.createdAt
  if (product) return product.createdAt
  return data.updatedAt
}
const getModDate = (article, product, data) => {
  if (article) return article.updatedAt
  if (product) return product.updatedAt
  return data.updatedAt
}

const GeneralStructuredMetadata = ({
  site,
  canonical,
  article,
  product,
  defaultTitle,
  data,
  title,
}) => {
  let articlePart = null
  if (article) {
    articlePart = {
      '@type': 'Article',
      headline: article.title,
      keywords: article.meta_description,
      datePublished: article.createdAt,
      dateModified: article.updatedAt,
      author: {
        '@id': `${uniformUrl(canonical, site.siteUrl)}#author`,
      },
      publisher: {
        '@id': `${site.siteUrl}/#organization`,
      },
      description: `${article.meta_description}`,
      name: article.meta_title,
      '@id': `${uniformUrl(canonical, site.siteUrl)}#richSnippet`,
      isPartOf: {
        '@id': `${uniformUrl(canonical, site.siteUrl)}#webpage`,
      },
      inLanguage: 'en-US',
      mainEntityOfPage: {
        '@id': `${uniformUrl(canonical, site.siteUrl)}#webpage`,
      },
    }
    if (article.hero_image_url) {
      articlePart.image = {
        '@id': article.hero_image_url,
      }
    }
  }
  const webPage = {
    '@type': 'WebPage',
    '@id': `${uniformUrl(canonical, site.siteUrl)}#webpage`,
    url: `${uniformUrl(canonical, site.siteUrl)}`,
    name: title || defaultTitle,
    datePublished: getDate(article, product, data),
    dateModified: getModDate(article, product, data),
    author: {
      '@id': `${uniformUrl(canonical, site.siteUrl)}#author`,
    },
    isPartOf: {
      '@id': `${site.siteUrl}/#website`,
    },
    inLanguage: 'en-US',
  }
  if (article.hero_image_url) {
    webPage.primaryImageOfPage = {
      '@id': article.hero_image_url,
    }
  }
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${site.siteUrl}/#organization`,
        name: site.name,
        url: site.siteUrl,
        logo: {
          '@type': 'ImageObject',
          '@id': `${site.siteUrl}/#logo`,
          url: 'https://sellusyourjewelry.com/wp-content/uploads/2019/12/photo_2019-12-26_17-32-13.jpg',
          name: site.title,
          caption: site.title,
          inLanguage: 'en-US',
          width: '1280',
          height: '356',
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${site.siteUrl}/#website`,
        url: site.siteUrl,
        name: site.title,
        publisher: {
          '@id': `${site.siteUrl}/#organization`,
        },
        inLanguage: 'en-US',
      },
      {
        '@type': 'ImageObject',
        '@id': `${site.siteUrl}/logo-900.jpg`,
        url: `${site.siteUrl}/logo-900.jpg`,
        width: '900',
        height: '235',
        caption: site.alternateName,
        inLanguage: 'en-US',
      },
      {
        '@type': 'Person',
        '@id': `${uniformUrl(canonical, site.siteUrl)}#author`,
        name: site.author,
        url: `${site.siteUrl}/about-us/`,
        image: {
          '@type': 'ImageObject',
          '@id': `${site.siteUrl}/keith.jpg`,
          url: `${site.siteUrl}/keith.jpg`,
          caption: site.author,
          inLanguage: 'en-US',
        },
        worksFor: {
          '@id': `${site.siteUrl}/#organization`,
        },
      },
      webPage,
      articlePart,
    ].filter(Boolean),
  })
}

const useSiteMetadata = () => {
  const { data, reviews } = useStaticQuery(
    graphql`
      query {
        reviews: allStrapiGoogleReview {
          sum: sum(field: rating)
          total: totalCount
        }
        data: site {
          siteMetadata {
            title
            description
            author
            siteUrl
            alternateName
            name
            addressCity
            addressState
            addressZip
            addressStreet
            phone
            email
            foundingYear
            legalName
            priceRange
            businessType
            image
            logo
            facebook
            twitter
            linkedin
            instagram
            youtube
            pinterest
            twitterUsername
          }
        }
      }
    `
  )
  const site = data.siteMetadata
  return { site, data, reviews }
}

const selectHeroImage = (gatsbyImage, type='full') => {
  // console.log('Getting content hero image', gatsbyImage, 'for type', type)
  // note - it will fall back to the closest size if the requested size is not available
  switch(type){
    case 'facebook':
      return getImageSize(gatsbyImage, 1200, 'jpg', IMAGE_TYPES.square)
    case 'twitter':
      return getImageSize(gatsbyImage, 4096, 'jpg', IMAGE_TYPES.square)
    default:
      return getImageSize(gatsbyImage, 4096, 'jpg', IMAGE_TYPES.regular)
  }
}


const getContentHeroAlt = (article, product, site) => {
  if (article) {
    return article.title
  }
  if (product && product.image ) {
    return article.name
  }
  return site.name
}
const getContentHero = (article, product, site, type='full') => {
  if (article) {
    if(article.heroImageUrl) {
      return article.heroImageUrl
    }
    if(article.hero_image_url) {
      return article.hero_image_url
    }
    if(article.heroImage) {
      return selectHeroImage(article.heroImage, type) || site.logo
    }
  }
  if (product && product.image ) {
    return selectHeroImage(product.image, type) || site.logo
  }
  return site.logo
}

const getTwitterProductCardMeta = ({ product }) => {
  const twitterMeta = []
  if (product && product.price && product.qty > 0) {
    twitterMeta.push({ name: 'twitter:data1', content: USD_P2(finalPrice(product)) })
    twitterMeta.push({ name: 'twitter:label1', content: 'Price' })
    twitterMeta.push({
      name: 'twitter:data2',
      content: product.brand && product.model ? `${product.brand} ${product.model}` : product.sku,
    })
    twitterMeta.push({
      name: 'twitter:label2',
      content: product.brand && product.model ? 'Brand' : 'SKU',
    })
  }
  return twitterMeta
}

const getTwitterAndFBMetadata = ({
  seo,
  site,
  lang,
  canonical,
  article,
  product,
  title,
  metaDescription,
  // content_hero_image,
  defaultTitle,
}) => {
  let ogType = 'website'
  if (article) ogType = 'article'
  if (product) ogType = 'product'
  const twitterMeta = getTwitterProductCardMeta(product)
  const twitter = seo?.metaSocial?.find(s => s.socialNetwork === 'Twitter') || {}
  const facebook = seo?.metaSocial?.find(s => s.socialNetwork === 'Facebook') || {}
  return [
    { property: 'og:site_name', content: site.alternateName },
    { property: 'og:locale', content: lang },
    { property: 'og:url', content: uniformUrl(canonical, site.siteUrl) },
    { property: 'og:title', content: facebook?.title || title || defaultTitle },
    { property: 'og:type', content: ogType },
    { property: 'og:description', content: facebook?.description || metaDescription },
    { property: 'og:image', content: facebook?.image?.facebookSocialImage || getContentHero(article, product, site, 'facebook') },
    { property: 'og:image:alt', content: getContentHeroAlt(article, product, site, 'facebook') || site.name },
    { name: 'twitter:card', content: product && product.qty > 0 ? 'product' : 'summary' },
    { name: 'twitter:creator', content: site.twitterUsername },
    { name: 'twitter:title', content: twitter?.title || title || defaultTitle },
    { name: 'twitter:description', content: twitter?.description || metaDescription },
    { name: 'twitter:image', content: twitter?.image?.twitterSocialImage || getContentHero(article, product, site, 'twitter') },
    { name: 'twitter:image:alt', content: getContentHeroAlt(article, product, site, 'facebook') || site.name },
    { name: 'description', content: metaDescription },
    ...twitterMeta,
  ]
}

const replaceMetaTag = (meta, tag) => {
  const { name, property, rel } = tag
  const existingTag = meta.findIndex(
    m =>
      (m.name === name && name) || (m.property === property && property) || (m.rel === rel && rel)
  )
  if (existingTag !== -1) {
    // meta.splice(existingTag, 1)
    meta[existingTag] = tag
  } else {
    meta.push(tag)
  }
}

const truncateCharacters = (str, maxLength = 255) => {
  if ((str || '').length <= maxLength) return str
  let trimmedString = str.substring(0, maxLength + 1)
  let lastSpace = trimmedString.lastIndexOf(' ')
  if (lastSpace > 0) {
    trimmedString = trimmedString.substring(0, lastSpace).trim()
  }
  return trimmedString.substr(0, maxLength)
}

const SeoRaw = ({
  description,
  lang = 'en',
  meta,
  title,
  canonical,
  noindex = false,
  product,
  article,
  seo,
  paginationTitle = ''
}) => {
  const { site, data, reviews } = useSiteMetadata()
  const defaultTitle = site?.title
  const paginationTitlePart = paginationTitle ? ` | ${paginationTitle} ` : ''
  const metaDescription = truncateCharacters(seo?.metaDescription || description || site.description, 160 - paginationTitlePart.length) + paginationTitlePart
  const endString = ' | Gray & Sons Jewelers'
  const endString2 = ' | Gray & Sons'
  if (title) title = `${title} ${paginationTitlePart}`
  if (seo?.metaTitle) {
    if (!seo.metaTitle.includes('|')) {
      title = `${seo.metaTitle} ${paginationTitlePart}`
    } else {
      title = seo.metaTitle.replace(/[|][^|]+$/, paginationTitlePart)
    }
  }
  title = title.trim()
  if (title.length + endString.length <= 70) {
    title += endString
  } else if (title.length + endString2.length <= 70) {
    title += endString2
  }
  // const content_hero_image = getContentHero(article, product, site)
  // preload important libraries
  const _link = []
  const _meta = (meta || []).concat(
    getTwitterAndFBMetadata({
      seo,
      site,
      lang,
      canonical,
      article,
      product,
      title,
      metaDescription,
      // content_hero_image,
      defaultTitle,
    })
  )
  if (seo?.metaRobots) {
    replaceMetaTag(_meta, { name: 'robots', content: seo.metaRobots })
  } else if (noindex) {
    replaceMetaTag(_meta, {
      name: 'robots',
      content: 'noindex, nofollow, nosnippet, noarchive, nocache',
    })
  }
  if (seo?.canonicalURL) {
    replaceMetaTag(_link, { rel: 'canonical', href: seo.canonicalURL })
  } else if (canonical) {
    replaceMetaTag(_link, { rel: 'canonical', href: uniformUrl(canonical, site.siteUrl) })
  }

  const scripts = [
    {
      type: 'application/ld+json',
      id: 'webpage-structured-metadata',
      innerHTML: WebPageStructuredMetadata({ site }),
    },
    {
      type: 'application/ld+json',
      id: 'local-business-structured-metadata',
      innerHTML: LocalBusinessStructuredMetadata({ site }),
    },
    {
      type: 'application/ld+json',
      id: 'organization-structured-metadata',
      innerHTML: OrganizationStructuredMetadata({ site, reviews }),
    },
    {
      type: 'application/ld+json',
      id: 'general-structured-metadata',
      innerHTML: GeneralStructuredMetadata({
        site,
        canonical,
        product,
        article,
        defaultTitle,
        data,
        title,
      }),
    },
  ]
  if (product) {
    scripts.push({
      type: 'application/ld+json',
      id: 'product-structured-metadata',
      innerHTML: ProductStructuredMetadata({ site, product }),
    })
  }
  if (seo?.structuredData?.internal?.content) {
    scripts.push({
      type: 'application/ld+json',
      id: 'structured-metadata',
      innerHTML: seo.structuredData.internal.content,
    })
  }

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title || defaultTitle}
      defer={false}
      meta={_meta}
      script={scripts}
    >
      <link rel="preconnect" href="https://cdn.grayandsons.com" />
      <link rel="preconnect" href="https://api.livechatinc.com" />
      <link rel="preconnect" href="https://cdn.livechatinc.com" />
      <link rel="preconnect" href="https://accounts.livechatinc.com" />
      <link rel="preconnect" href="https://queue.livechatinc.com" />
      <link rel="preconnect" href="https://cdn.livechat-files.com" />
      <link rel="dns-prefetch" href="https://cdn.grayandsons.com" />
      <link rel="dns-prefetch" href="https://api.livechatinc.com" />
      <link rel="dns-prefetch" href="https://cdn.livechatinc.com/" />
      <link rel="dns-prefetch" href="https://accounts.livechatinc.com" />
      <link rel="dns-prefetch" href="https://queue.livechatinc.com" />
      <link rel="dns-prefetch" href="https://cdn.livechat-files.com" />
      {/* <link rel="preload" href="https://cdn.livechatinc.com/tracking.js" as="script" /> */}
      <link rel="preload" href='//www.googletagmanager.com/gtag/js?id=G-1S8B9HGMB3' as="script" />
      {/* NOTE: Merge AW-1061399325 Tag with G-1S8B9HGMB3 and remove one tag script (below) */} 
      {/* <link rel="preload" href={'//www.googletagmanager.com/gtag/js?id=AW-1061399325&l=dataLayer&cx=c'} as="script" /> */}
      {/* <link rel="preload" href='//www.googletagmanager.com/gtag/destination?id=AW-1061399325&l=dataLayer&cx=c' as="script" /> */}
      {/* <link rel="preload" href="//www.googletagmanager.com/gtm.js?id=GTM-K25RWJ" as="script" /> */}
      <link rel="preload" href={'//cdn.callrail.com/companies/267670386/7ad9f09f303977651ce0/12/swap.js'} as="script" />
      <link rel="preload" href="//static.klaviyo.com/onsite/js/klaviyo.js?company_id=JHEYDB" as="script" />
      <link rel="preload" href="//bat.bing.com/bat.js" as="script" />
      <link rel="preload" href="//www.clarity.ms/tag/uet/187029939" as="script" />
      <link rel="preload" href="//dynamic.criteo.com/js/ld/ld.js?a=61488" as="script" />
      {_link.map((l, i) => (
        <link key={i} {...l} />
      ))}
      
    </Helmet>
  )
}
const Seo = memo(SeoRaw)

export const query = graphql`
  fragment SEO on STRAPI__COMPONENT_SHARED_SEO {
    metaRobots
    metaViewport
    keywords
    canonicalURL
    structuredData {
      internal {
        content
      }
    }
    metaTitle
    metaDescription
    metaSocial {
      socialNetwork
      title
      description
      image {
        twitterSocialImage
        facebookSocialImage
      }
    }
  }
`

SeoRaw.defaultProps = Seo.defaultProps = {
  lang: 'en',
  meta: [],
  description: '',
  title: '',
  canonical: '',
  noindex: false,
  product: false,
  article: false,
}

SeoRaw.propTypes = Seo.propTypes = {
  seo: PropTypes.shape({
    metaRobots: PropTypes.string,
    metaViewport: PropTypes.string,
    keywords: PropTypes.string,
    canonicalURL: PropTypes.string,
    structuredData: PropTypes.shape({
      internal: PropTypes.shape({
        content: PropTypes.string,
      }),
    }),
    metaTitle: PropTypes.string.isRequired,
    metaDescription: PropTypes.string.isRequired,
    metaSocial: PropTypes.arrayOf(
      PropTypes.shape({
        socialNetwork: PropTypes.oneOf(['Facebook', 'Twitter']),
        title: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.shape({
          twitterSocialImage: PropTypes.string,
          facebookSocialImage: PropTypes.string,
        }),
      })
    ),
  }),
  lang: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  canonical: PropTypes.string,
  noindex: PropTypes.bool,
  product: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  article: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  paginationTitle: PropTypes.string,
}

export default Seo
